<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import MessageTemplate from '@/components/Shared/Input/Template/MessageTemplate.vue';
import { useAddressPredictions } from '@/composables/useAddressPredictions';
import {
  type CityStateSuggestion,
  toCityStateSuggestions,
} from '@/core/jobs/copilot-activation/utils/city-state-prediction.util';
import type { CityState } from '@/core/shared/geolocation/geolocation.persistence';
import { SearchService } from '@/core/shared/search/search.service';
import { isRequired } from '@/utils/forms';

defineProps<{
  modelValue: string | null;
  disabled: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', city: string): void;
  (e: 'select', pair: CityState): void;
}>();

const searchService = new SearchService();
const {
  items: predictions,
  clear: clearPredictions,
  search: searchPredictions,
} = useAddressPredictions((address) => searchService.getAddressPredictions(address));

const suggestions = computed(() => toCityStateSuggestions(predictions.value));
const field = ref<{ focus: () => void } | null>(null);
const isFocused = ref(false);
const menu = ref(false);
// Each keystroke empties the list and Vuetify closes an empty menu; reopen it when results land.
watch(suggestions, (list) => {
  if (isFocused.value && list.length) menu.value = true;
});
// Picking a suggestion echoes its city back through the search text; that is not the user
// typing, so it must not start another lookup.
let pickedCity: string | null = null;

function isSuggestion(value: unknown): value is CityStateSuggestion {
  return typeof value === 'object' && value !== null && 'state' in value && 'city' in value;
}

function handleUpdate(value: unknown) {
  if (isSuggestion(value)) {
    pickedCity = value.city;
    clearPredictions();
    emit('select', { city: value.city, state: value.state });
    return;
  }

  emit('update:modelValue', typeof value === 'string' ? value : '');
}

function handleSearch(value: string | null) {
  const text = value ?? '';
  if (text === pickedCity) return;
  pickedCity = null;
  // Store hydration also moves the search text; only typing should hit the predictions API.
  if (!isFocused.value) return;

  searchPredictions(text);
}

function handleFocusout() {
  isFocused.value = false;
  pickedCity = null;
}

defineExpose({ focus: () => field.value?.focus() });
</script>

<template>
  <v-combobox
    ref="field"
    v-model:menu="menu"
    :model-value="modelValue || null"
    data-test="job-city"
    :items="suggestions"
    item-title="city"
    item-value="key"
    variant="outlined"
    density="compact"
    placeholder="City"
    menu-icon=""
    return-object
    :menu-props="{ maxWidth: '536px' }"
    :rules="[isRequired]"
    :disabled="disabled"
    validate-on="submit"
    no-filter
    hide-no-data
    @update:model-value="handleUpdate"
    @update:search="handleSearch"
    @focusin="isFocused = true"
    @focusout="handleFocusout"
  >
    <!-- The field shows the bare city once picked; the list needs the state to tell pairs apart. -->
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" :title="item.raw.title" data-test="job-city-suggestion" />
    </template>
    <template #message="{ message }">
      <MessageTemplate :message="message" />
    </template>
  </v-combobox>
</template>
